/**
 * Alias editor dialog.
 *
 * View, add, and remove the import ALIASES of an account — the raw sheet
 * names that match incoming upline rows to this account on import. Until now
 * aliases could only be managed inside the setup wizard; this modal surfaces
 * the same controls from the accounts-management screen.
 *
 * Phase 4a adds the impact view: per alias, "which posted weeks did this
 * name touch?" — linking straight into Reopen & Fix for a suspect alias.
 *
 * Endpoints (confirmed in src/http/routes/accounts.ts):
 *   - GET    /api/accounts/:id/aliases     → AccountAlias[]
 *   - POST   /api/accounts/:id/aliases     { rawName } → AccountAlias (201)
 *   - DELETE /api/aliases/:aliasId         → 204 (PIN-gated)
 *   - GET    /api/aliases/:aliasId/impact  → AliasImpact (read-only)
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CalendarSearch, X } from 'lucide-react';
import { api, ApiError } from '../api';
import type { Account, AccountAlias, AliasImpact } from '../api';
import { usePinGate } from '../auth/pinGate';
import {
  Button,
  EmptyState,
  ErrorBanner,
  Spinner,
  TextInput,
} from '../ui/primitives';
import { Modal } from '../ui/Modal';

export interface AliasEditorDialogProps {
  account: Account;
  onClose: () => void;
}

export function AliasEditorDialog({ account, onClose }: AliasEditorDialogProps) {
  const qc = useQueryClient();
  const { withPin } = usePinGate();
  const [rawName, setRawName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [impactAliasId, setImpactAliasId] = useState<number | null>(null);

  const queryKey = ['account-aliases', account.id] as const;

  const listQuery = useQuery({
    queryKey,
    queryFn: () =>
      api.get<AccountAlias[]>(`/api/accounts/${account.id}/aliases`),
  });

  const addMut = useMutation({
    mutationFn: (name: string) =>
      api.post<AccountAlias>(`/api/accounts/${account.id}/aliases`, {
        rawName: name,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
      setRawName('');
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/aliases/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const onAdd = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = rawName.trim();
    if (!trimmed) {
      setError('Enter a name to add as an alias.');
      return;
    }
    addMut.mutate(trimmed);
  };

  const onRemove = (id: number) => {
    setError(null);
    void withPin(() => removeMut.mutateAsync(id));
  };

  const aliases = listQuery.data ?? [];

  return (
    <Modal onClose={onClose}>
        <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
          <h2 className="text-small font-semibold text-ink">
            Aliases — {account.canonicalName}
          </h2>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-caption text-ink-muted">
            Aliases are how imported sheet rows match to this account. Removing
            one only stops FUTURE imports from matching that name — weeks that
            are already posted don&apos;t change. To correct a posted week, use
            &ldquo;Which weeks?&rdquo; below and open it with Reopen &amp; Fix.
          </p>

          <form className="mt-3 flex items-end gap-2" onSubmit={onAdd}>
            <div className="flex-1">
              <TextInput
                label="Add alias"
                value={rawName}
                onChange={(e) => setRawName(e.target.value)}
                placeholder="Raw sheet name, e.g. SAURAB CLICK 01"
                name="alias-raw-name"
              />
            </div>
            <Button type="submit" disabled={addMut.isPending}>
              {addMut.isPending ? <Spinner /> : 'Add'}
            </Button>
          </form>

          {error && (
            <div className="mt-3">
              <ErrorBanner>{error}</ErrorBanner>
            </div>
          )}

          <div className="mt-4">
            <div className="mb-2 text-caption font-semibold uppercase tracking-[0.1em] text-ink-muted">
              Current aliases
            </div>

            {listQuery.isLoading ? (
              <div className="flex items-center gap-2 py-8 text-small text-ink-muted">
                <Spinner />
                Loading aliases…
              </div>
            ) : listQuery.isError ? (
              <ErrorBanner>
                {listQuery.error instanceof ApiError
                  ? listQuery.error.message
                  : 'Failed to load aliases.'}
              </ErrorBanner>
            ) : aliases.length === 0 ? (
              <EmptyState
                icon={X}
                title="No aliases yet"
                description="Add the raw sheet names that should match imported rows to this account."
              />
            ) : (
              <ul className="space-y-1.5">
                {aliases.map((alias) => (
                  <li
                    key={alias.id}
                    className="rounded-[14px] border border-border bg-surface-raised px-3 py-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="min-w-0 break-words text-body text-ink">
                        {alias.rawName}
                      </span>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <button
                          type="button"
                          aria-label={`Show posted weeks touched by ${alias.rawName}`}
                          title="Which posted weeks did this name touch?"
                          onClick={() =>
                            setImpactAliasId((cur) =>
                              cur === alias.id ? null : alias.id,
                            )
                          }
                          className={[
                            'inline-flex h-8 items-center gap-1 rounded-full border border-border px-2.5 text-caption shadow-sm transition-colors',
                            impactAliasId === alias.id
                              ? 'bg-surface text-ink'
                              : 'bg-surface text-ink-muted hover:text-ink',
                          ].join(' ')}
                        >
                          <CalendarSearch className="h-3.5 w-3.5" strokeWidth={2} />
                          Which weeks?
                        </button>
                        <button
                          type="button"
                          aria-label={`Remove alias ${alias.rawName}`}
                          title="Remove alias"
                          disabled={removeMut.isPending}
                          onClick={() => onRemove(alias.id)}
                          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-surface text-ink-muted shadow-sm transition-colors hover:bg-red-50 hover:text-neg disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <X className="h-4 w-4" strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                    {impactAliasId === alias.id && (
                      <AliasImpactPanel aliasId={alias.id} />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
    </Modal>
  );
}

/**
 * "Which posted weeks did this name touch?" — read-only impact list with a
 * Reopen & Fix deep link per week (Phase 3's /batches/:id?fix=1).
 */
function AliasImpactPanel({ aliasId }: { aliasId: number }) {
  const impactQ = useQuery({
    queryKey: ['alias-impact', aliasId],
    queryFn: () => api.get<AliasImpact>(`/api/aliases/${aliasId}/impact`),
  });

  if (impactQ.isLoading) {
    return (
      <div className="mt-2 flex items-center gap-2 border-t border-border pt-2 text-caption text-ink-muted">
        <Spinner /> Checking posted weeks…
      </div>
    );
  }
  if (impactQ.isError) {
    return (
      <div className="mt-2 border-t border-border pt-2">
        <ErrorBanner>
          {impactQ.error instanceof ApiError
            ? impactQ.error.message
            : 'Failed to check posted weeks.'}
        </ErrorBanner>
      </div>
    );
  }

  const batches = impactQ.data?.postedBatches ?? [];
  if (batches.length === 0) {
    return (
      <p className="mt-2 border-t border-border pt-2 text-caption text-ink-muted">
        This name doesn&apos;t appear in any posted week — deleting the alias
        is enough.
      </p>
    );
  }

  return (
    <div className="mt-2 border-t border-border pt-2">
      <p className="text-caption text-ink-muted">
        This name appears in {batches.length} posted{' '}
        {batches.length === 1 ? 'week' : 'weeks'}. If the alias was wrong,
        re-check each one — the link opens the week with Reopen &amp; Fix:
      </p>
      <ul className="mt-1.5 space-y-1">
        {batches.map((b) => (
          <li key={b.batchId} className="flex items-center justify-between gap-2">
            <span className="text-caption text-ink">
              Week of {b.weekMonday} — {b.uplineName} ({b.rowCount}{' '}
              {b.rowCount === 1 ? 'row' : 'rows'})
            </span>
            <Link
              to={`/batches/${b.batchId}?fix=1`}
              className="shrink-0 text-caption font-medium text-sec-accounts underline underline-offset-2 hover:opacity-80"
            >
              Reopen &amp; fix
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
